import { useEffect } from 'react'
import { useReactFlow, useStore } from '@xyflow/react'

/**
 * RightDragPan — right-button drag pans a graph canvas; a right CLICK (no
 * movement) still opens the canvas's menu. Render it as a child of a
 * <ReactFlow>; it owns no DOM.
 *
 * WHY NOT React Flow's own `panOnDrag={[2]}`: that was tried and dropped
 * (graphInteractionProps.js). The browser fires `contextmenu` on the right
 * button — on mousedown on macOS/Linux, on release on Windows — and that event
 * stole the pointer from d3-zoom, so the pan never saw its pointerup and stuck
 * to the cursor. Here the pan is ours (setViewport on window pointermove), and
 * EVERY trusted `contextmenu` that belongs to a right press is swallowed in the
 * capture phase. If the press did not move, a `contextmenu` is re-dispatched on
 * the original target at release — so the canvas, node and edge menus open
 * exactly as before, on every platform, and only for a click.
 *
 * A right-click in a text field keeps the browser's own menu (copy/paste).
 * A context-menu KEY press has no pointer press and passes straight through.
 */
const MOVE_PX = 4

export function RightDragPan() {
  const rf = useReactFlow()
  const domNode = useStore(s => s.domNode)

  useEffect(() => {
    if (!domNode) return
    let press = null          // the right press being tracked
    let swallowUntil = 0      // a trusted contextmenu before this is the press's own
    let redispatching = false

    const isField = (el) => el?.closest?.('input, textarea, select, [contenteditable="true"]')

    const onMove = (e) => {
      if (!press) return
      const dx = e.clientX - press.x
      const dy = e.clientY - press.y
      if (!press.moved && Math.hypot(dx, dy) < MOVE_PX) return
      press.moved = true
      // Closed hand over everything while panning (graphEditor.css) — an inline
      // style on the root would lose to the pane/node cursor rules underneath.
      domNode.classList.add('aos-panning')
      rf.setViewport({ x: press.vx + dx, y: press.vy + dy, zoom: press.zoom })
    }

    const end = (e) => {
      if (!press || e.button !== 2) return
      const p = press
      press = null
      window.removeEventListener('pointermove', onMove, true)
      window.removeEventListener('pointerup', end, true)
      domNode.classList.remove('aos-panning')
      swallowUntil = performance.now() + 500
      if (p.moved) return
      redispatching = true
      try {
        p.target.dispatchEvent(new MouseEvent('contextmenu', {
          bubbles: true, cancelable: true, button: 2, buttons: 0,
          clientX: e.clientX, clientY: e.clientY, screenX: e.screenX, screenY: e.screenY,
          shiftKey: e.shiftKey, ctrlKey: e.ctrlKey, altKey: e.altKey, metaKey: e.metaKey,
        }))
      } finally {
        redispatching = false
      }
    }

    const onDown = (e) => {
      if (e.button !== 2 || isField(e.target)) return
      const vp = rf.getViewport()
      press = { x: e.clientX, y: e.clientY, vx: vp.x, vy: vp.y, zoom: vp.zoom, target: e.target, moved: false }
      window.addEventListener('pointermove', onMove, true)
      window.addEventListener('pointerup', end, true)
    }

    const onContextMenu = (e) => {
      if (redispatching || !e.isTrusted || isField(e.target)) return
      if (press || performance.now() < swallowUntil) {
        e.preventDefault()
        e.stopPropagation()
      }
    }

    domNode.addEventListener('pointerdown', onDown, true)
    domNode.addEventListener('contextmenu', onContextMenu, true)
    return () => {
      domNode.removeEventListener('pointerdown', onDown, true)
      domNode.removeEventListener('contextmenu', onContextMenu, true)
      window.removeEventListener('pointermove', onMove, true)
      window.removeEventListener('pointerup', end, true)
      domNode.classList.remove('aos-panning')
    }
  }, [domNode, rf])

  return null
}
